/** Reverificarea dimensiunilor modelului încărcat față de fișa JSON (aceleași reguli ca scriptul Blender, în spațiul glTF). */
import { Box3, Mesh, Object3D, Vector3 } from 'three';
import { tyreDiameterM } from '../data/calc';
import type { Issue, VehicleDefinition } from '../data/types';
import { CONVENTION, isRequired } from './convention';

export function checkModel(root: Object3D, v: VehicleDefinition): Issue[] {
  const out: Issue[] = [];
  const tol = CONVENTION.toleranceMm / 1000;
  const err = (code: string, path: string, message: string) => out.push({ severity: 'error', code, path, message });
  root.updateMatrixWorld(true);
  const byName = new Map<string, Object3D>();
  root.traverse((o) => { if (o.name && !byName.has(o.name)) byName.set(o.name, o); });
  for (const p of CONVENTION.parts) if (isRequired(p, v) && !byName.has(p.name)) err('names.missing', p.name, `lipsește ${p.name}`);

  const excluded = new Set<Object3D>();
  for (const g of ['interior_detail', 'interior_simple', 'engine_bay', 'mirror_L', 'mirror_R', 'antenna']) byName.get(g)?.traverse((o) => excluded.add(o));
  const box = new Box3(), tmp = new Box3();
  root.traverse((o) => {
    if (!(o as Mesh).isMesh || excluded.has(o)) return;
    tmp.setFromObject(o);
    box.union(tmp);
  });
  const size = box.getSize(new Vector3());
  const d = v.dimensions;
  for (const [label, got, want] of [['lungimea', size.z, d.lengthMm], ['lățimea', size.x, d.widthMm], ['înălțimea', size.y, d.heightMm]] as const) {
    if (Math.abs(got - want / 1000) > tol) err('dims.body', 'dimensions', `${label} modelului e ${(got * 1000).toFixed(0)} mm, fișa cere ${want} mm`);
  }
  if (Math.abs(box.min.y) > tol) err('dims.ground', 'dimensions', `cel mai jos punct e la ${(box.min.y * 1000).toFixed(0)} mm, nu pe sol`);
  const w = (k: string) => byName.get(`wheel_${k}`)?.getWorldPosition(new Vector3());
  const [fl, fr, rl, rr] = ['FL', 'FR', 'RL', 'RR'].map(w);
  if (fl && fr && rl && rr) {
    const wb = (fl.z - rl.z + fr.z - rr.z) / 2;
    if (Math.abs(wb - d.wheelbaseMm / 1000) > tol) err('dims.wheelbase', 'dimensions.wheelbaseMm', `ampatamentul modelului e ${(wb * 1000).toFixed(0)} mm, fișa cere ${d.wheelbaseMm}`);
    if (Math.abs(Math.abs(fl.x - fr.x) - d.trackFrontMm / 1000) > tol) err('dims.track', 'dimensions.trackFrontMm', `ecartamentul față e ${(Math.abs(fl.x - fr.x) * 1000).toFixed(0)} mm, fișa cere ${d.trackFrontMm}`);
    if (Math.abs(Math.abs(rl.x - rr.x) - d.trackRearMm / 1000) > tol) err('dims.track', 'dimensions.trackRearMm', `ecartamentul spate e ${(Math.abs(rl.x - rr.x) * 1000).toFixed(0)} mm, fișa cere ${d.trackRearMm}`);
    if (fl.x < 0) err('axes', 'wheel_FL', 'roata stânga trebuie să fie pe +X în glTF (fața spre +Z)');
    if (fl.z < rl.z) err('axes', 'wheel_FL', 'fața mașinii trebuie să fie spre +Z în glTF');
  }
  // pivoturile: balamalele trebuie să fie pe muchia corectă, roțile în centru (în glTF fața e spre +Z)
  const pivotRule: Record<string, (p: Vector3, b: Box3) => boolean> = {
    'hinge-front': (p, b) => Math.abs(p.z - b.max.z) <= 0.04,
    'hinge-rear': (p, b) => Math.abs(p.z - b.min.z) <= 0.04,
    'hinge-top': (p, b) => Math.abs(p.y - b.max.y) <= 0.06,
    top: (p, b) => Math.abs(p.y - b.max.y) <= 0.012,
    center: (p, b) => p.distanceTo(b.getCenter(new Vector3())) <= 0.012
  };
  for (const p of CONVENTION.parts) {
    const o = byName.get(p.name);
    const rule = pivotRule[p.pivot];
    if (!o || !rule) continue;
    if (!rule(o.getWorldPosition(new Vector3()), new Box3().setFromObject(o))) err('pivot.' + p.pivot, p.name, `${p.name}: pivotul nu e pe ${p.pivot}`);
  }
  for (const k of ['FL', 'FR', 'RL', 'RR']) {
    const t = byName.get(`tyre_${k}`);
    const wn = byName.get(`wheel_${k}`);
    if (t && wn && wn.getWorldPosition(new Vector3()).distanceTo(new Box3().setFromObject(t).getCenter(new Vector3())) > 0.003) err('pivot.wheel', `wheel_${k}`, `wheel_${k} nu are originea în centrul roții`);
  }
  for (const k of ['FL', 'FR', 'RL', 'RR']) {
    const t = byName.get(`tyre_${k}`);
    if (!t) continue;
    const dia = new Box3().setFromObject(t).getSize(new Vector3()).y;
    const want = tyreDiameterM(v.tyres[k[0] === 'F' ? 'front' : 'rear'].size) ?? 0;
    if (Math.abs(dia - want) > tol) err('dims.tyre', `tyre_${k}`, `anvelopa ${k} are ${(dia * 1000).toFixed(0)} mm, fișa cere ${(want * 1000).toFixed(0)} mm`);
  }
  return out;
}
